'use client';

import { useState } from 'react';
import posthog from 'posthog-js';
import { updateQuiz } from '@/app/actions/quizzes';
import { callAction } from '@/lib/call-action';
import { toUpdateBody } from '@/lib/quiz/create-body';
import { Field, type EditableQuiz } from '@/components/builder/QuizFields';
import { QuestionSwaps } from '@/components/builder/QuestionSwaps';
import { QuizFinishedCard } from '@/components/quiz/QuizFinishedCard';
import type { QuizTutorDto } from '@peach/types/quizzes/quiz.dto.js';
import { kitCopyOf } from '@/lib/quiz/kit-copy';
import { QUIZ_KIT_MAX_CHARS, type QuizKitCopy } from '@peach/types/quizzes/generated-quiz.dto.js';

/**
 * A live quiz edited where it stands (her storefront, or its post on /reply): the hook, each
 * question (Edit in place, or Swap for a new one written on the quiz's own source) and the carousel's last line.
 */
export function QuizEditor({
  quiz,
  owner,
  source,
  onClose,
}: {
  quiz: QuizTutorDto;
  owner: string;
  /** Where it was edited, for `owner_quiz_edited`. */
  source: 'storefront' | 'replies';
  /** `saved` is false on Cancel, so a caller refreshes only for a real change. */
  onClose: (saved: boolean) => void;
}) {
  const [draft, setDraft] = useState<EditableQuiz>({
    title: quiz.title,
    questions: quiz.questions,
  });
  // Only a quiz that HAS a kit gets the line: hand-filling the stock one for a quiz
  // without a kit would answer the "write a new quiz" ask falsely. A comment-word door opens as the stock line.
  const [kit, setKit] = useState<QuizKitCopy | undefined>(quiz.kit && kitCopyOf(quiz.kit));
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const save = async () => {
    if (kit && !kit.door.trim()) {
      setFailed('The last slide needs a line.');
      return;
    }
    setSaving(true);
    setFailed(null);
    const result = await callAction(() => updateQuiz(quiz.id, toUpdateBody(draft, kit)));
    if (!result.success) {
      setSaving(false);
      setFailed(result.error);
      return;
    }
    posthog.capture('owner_quiz_edited', {
      creator_username: owner,
      provider_username: owner,
      tutor_username: owner,
      source,
    });
    onClose(true);
  };

  return (
    <QuizFinishedCard>
      <Field label="Hook" value={draft.title} onChange={(title) => setDraft({ ...draft, title })} />
      <QuestionSwaps quiz={draft} source={quiz} onChange={setDraft} />
      {kit && (
        <Field
          label="Last slide"
          value={kit.door}
          maxLength={QUIZ_KIT_MAX_CHARS.door}
          onChange={(door) => setKit({ ...kit, door })}
        />
      )}
      {failed && (
        <p
          aria-live="polite"
          className="rounded-xl bg-[rgba(0,0,0,0.3)] p-3 text-p-3 font-semibold text-white"
        >
          {failed}
        </p>
      )}
      <div className="mt-1 flex gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void save()}
          className="mm-btn sf-cta sf-cta-priced flex-1 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        {/* Card ink, not `sf-ghost`: that is a ground control, and its ink vanished on the black card. */}
        <button
          type="button"
          onClick={() => onClose(false)}
          className="mm-btn rounded-[var(--sf-radius)] border-[1.5px] border-dashed border-white/40 px-4 text-p-3 font-bold text-white"
        >
          Cancel
        </button>
      </div>
    </QuizFinishedCard>
  );
}
